// File: src/App.js
import React, { useEffect, useContext, useState } from 'react';
import {
  Routes,
  Route,
  Outlet,
  useSearchParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import usePageTracker from './hooks/usePageTracker';
import RequireAuth from './components/AuthPages/RequireAuth';
import RequireAuthShopify from './components/AuthPages/RequireAuthShopify';
import { SiteProvider } from './context/SiteContext';
import OAuthRedirectHandler from './components/AuthPages/OAuthRedirectHandler';
import ConnectWooCommerce from './components/ConnectWooCommerce';
import ConnectBigCommerce from './components/ConnectBigCommerce';
import { DataCacheProvider } from './context/DataCacheProvider';
import AppWarmup from './warmup/AppWarmup';
import { SubscriptionProvider } from './context/SubscriptionContext';
import RequireSubscription from './components/AuthPages/RequireSubscription';
import { AuthContext } from './context/AuthContext';
import { useApiClient } from './utils/apiClient';
import { AppProvider as PolarisProvider } from '@shopify/polaris';
import EmbeddedAuthRedirect from './pages/EmbeddedAuthRedirect';
import { useShopifyAuth } from './hooks/useShopifyAuth';

// Navigation context import
import { NavigationProvider } from './context/NavigationContext';

// Public pages
import LandingPage from './pages/LandingPage';
import LoginPage from './components/AuthPages/LoginPage';
import SignupPage from './components/AuthPages/SignupPage';
import ForgotPasswordPage from './components/AuthPages/ForgotPasswordPage';
import ResetPasswordPage from './components/AuthPages/ResetPasswordPage';
import HowItWorks from './pages/Register/HowItWorks';
import EverythingYouNeed from './pages/Register/EverythingYouNeed';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfServicePage from './pages/TermsOfServicePage';
import InstallationGuide from './pages/InstallationGuide';
import LinkShopifyAccount from './pages/linkShopify';

// Protected pages
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import SessionsPage from './pages/SessionsPage';
import SessionDetail from './pages/SessionDetail';
import DropOffsPage from './pages/DropOffsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import SettingsPage from './pages/SettingsPage';
import AddWebsitePage from './pages/AddWebsitePage';
import OptimizeSitePage from './pages/OptimizeSitePage';
import ConnectShopifyPage from './components/ConnectShopifyPage';
import InstallCustomScript from './pages/InstallCustomScript';
import ConnectFailurePage from './pages/ConnectFailurePage';
import ConnectSuccessPage from './pages/ConnectSuccessPage';
import SetUpStanStore from './pages/SetUpStanStore';
import PerformancePage from './pages/Performance';
import BusinessPage from './pages/Business';
import FactsPage from './pages/Facts';
import IntegrateGa4Only from './pages/IntegrateGa4Only';
import BigCommerceLanding from './pages/BigCommerceLanding';
import BigCommerceIntegrationsPage from './pages/BigCommerceIntegrationsPage';
import LinkBigCommerceAccount from './pages/LinkBigCommerceAccount';
import BillingSuccess from './components/billing/SuccessPage';
import ShopifyInstallHandler from './pages/ShopifyInstallHandler';
import ConfirmSiteReplace from './pages/ConfirmSiteReplace';
import CustomerVoicePage from './pages/CustomerVoicePage';
import CustomerDashboard from './pages/CustomerDashboard';
import UXInsightsPage from './pages/UXInsightsPage';
import PerformanceDashboard from './pages/PerformanceDashboard';
import ReportsPage from './pages/ReportsPage';

// Simple component to redirect to external URL
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
}

function AppContent() {
  const authContext = useContext(AuthContext);
  useApiClient(authContext);
  usePageTracker();

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const host = searchParams.get('host');
  const shop = searchParams.get('shop');
  const isEmbedded = !!(host && shop);

  // Trigger Shopify auth if we're in embedded context
  const { loading: shopifyAuthLoading } = useShopifyAuth();

  // Loading state for embedded routing decisions
  const [isCheckingRoute, setIsCheckingRoute] = useState(isEmbedded);
  // State for showing the "opening full dashboard" redirect UI
  const [showExternalRedirect, setShowExternalRedirect] = useState(false);
  const [externalRedirectUrl, setExternalRedirectUrl] = useState('');

  // Determine which Auth wrapper to use
  const AuthWrapper = isEmbedded ? RequireAuthShopify : RequireAuth;

  // Error boundary for chunk load errors
  useEffect(() => {
    const handleError = (event) => {
      if (event.error?.message?.includes('INVALID_ORIGIN')) {
        event.preventDefault();
        return;
      }
      
      if (event.error?.name === 'ChunkLoadError') {
        window.location.reload();
        event.preventDefault();
      }
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Route guard - Wait for Shopify auth before routing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shop = params.get('shop');
    const host = params.get('host');
    
    // Not embedded? No loading needed, render routes immediately
    if (!shop || !host) {
      setIsCheckingRoute(false);
      return;
    }

    // Wait for Shopify auth to complete before routing
    if (shopifyAuthLoading) {
      return;
    }

    const currentPath = location.pathname;
    
    // Define paths that are allowed to load directly (don't redirect these)
    const allowedPaths = [
      '/dashboard',
      '/integrations',
      '/settings',
      '/home',
      '/shopify/install',
      '/shopify/connect',
      '/ConnectFailurePage',
      '/ConnectSuccessPage',
      '/sites',
      '/customer-voice',
      '/sessions',
      '/dropoffs',
      '/UXInsightsPage',
      '/performanceDashboard',
      '/customerDashboard',
      '/business',
      '/facts',
      '/performance',
      '/reports',

    ];
    
    // Check if current path is in the allowed list
    const isAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));
    
    // If we're on an allowed path, let it load normally
    if (isAllowedPath) {
      setIsCheckingRoute(false);
      return;
    }
    
    // For root path '/' or other paths, check status and route accordingly
    setIsCheckingRoute(true);
    
    fetch(`${process.env.REACT_APP_API_URL}/shopify/check-status?shop=${shop}`)
      .then(res => res.json())
      .then(status => {
        if (status.is_first_install) {
          navigate(`/shopify/install?${params.toString()}`, { replace: true });
        } else if (status.installed && status.setup_complete) {
          // Show the "Opening Full Dashboard" UI
          setShowExternalRedirect(true);
          return; // Don't setIsCheckingRoute(false)
        } else if (status.installed && !status.setup_complete) {
          navigate(`/integrations?${params.toString()}`, { replace: true });
        } else {
          navigate(`/shopify/install?${params.toString()}`, { replace: true });
        }
        
        setIsCheckingRoute(false);
      })
      .catch(err => {
        console.error('[RouteGuard] Status check failed:', err);
        navigate(`/shopify/install?${params.toString()}`, { replace: true });
        setIsCheckingRoute(false);
      });
  }, [location.pathname, location.search, navigate, shopifyAuthLoading]);

  // Build external redirect URL when we need to redirect and have auth token
  useEffect(() => {
    if (showExternalRedirect && shop) {
      if (authContext?.token) {
        const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://baobrain.com';
        const fullDashboardUrl = new URL('/embedded-login', FRONTEND_URL);
        fullDashboardUrl.searchParams.set('token', authContext.token);
        fullDashboardUrl.searchParams.set('redirect', '/dashboard');
        fullDashboardUrl.searchParams.set('shop', shop);
        
        setExternalRedirectUrl(fullDashboardUrl.toString());
      }
    }
  }, [showExternalRedirect, authContext?.token, shop]);

  // Show loading state while checking route or waiting for Shopify auth
  if (isCheckingRoute || (isEmbedded && shopifyAuthLoading)) {
    return (
      <PolarisProvider i18n={{}}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: '#f6f6f7',
          fontFamily: '-apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, sans-serif',
        }}>
          {showExternalRedirect ? (
            // External redirect UI
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '32px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 20px',
                background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 3H21V9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 14L21 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: '#1a1a1a' }}>
                Opening Full Dashboard
              </h2>
              <p style={{ margin: '0 0 24px', color: '#637381', fontSize: '14px', lineHeight: '1.5' }}>
                For the best experience, BaoBrain opens in a new tab with full features.
              </p>
              
              {externalRedirectUrl ? (
                <a
                  href={externalRedirectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  ref={(el) => {
                    if (el) setTimeout(() => el.click(), 300);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '14px 24px',
                    background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '16px',
                    boxShadow: '0 4px 12px rgba(106, 13, 173, 0.3)',
                  }}
                >
                  Open Full Dashboard
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              ) : (
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e5e5e5',
                  borderTop: '3px solid #6A0DAD',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto',
                }} />
              )}
              
              <p style={{ color: '#9ca3af', marginTop: '16px', marginBottom: 0, fontSize: '13px' }}>
                {externalRedirectUrl ? "Click the button if a new tab doesn't open automatically" : 'Preparing redirect...'}
              </p>
            </div>
          ) : (
            // Loading UI
            <>
              <div style={{
                width: '48px',
                height: '48px',
                border: '3px solid #e5e5e5',
                borderTop: '3px solid #6A0DAD',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <p style={{ marginTop: '16px', color: '#637381', fontSize: '14px' }}>
                {shopifyAuthLoading ? 'Authenticating with Shopify...' : 'Loading BaoBrain...'}
              </p>
            </>
          )}
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </PolarisProvider>
    );
  }


  // Define the routes
  const appRoutes = (
    <Routes>
      {/* OAuth callback */}
      <Route path="/oauth-redirect" element={<OAuthRedirectHandler />} />

      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/privacyPolicy" element={<PrivacyPolicy />} />
      <Route path="/TermsOfServicePage" element={<TermsOfServicePage />} />
      <Route path="/ContactUs" element={<ExternalRedirect to="https://app.baobrain.com/contact" />} />
      <Route path="/link-shopify-account" element={<LinkShopifyAccount />} />
      <Route path="/InstallationGuide" element={<InstallationGuide />} />
      <Route path="/EverythingYouNeed" element={<EverythingYouNeed />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/shopify/install" element={<ShopifyInstallHandler />} />
      <Route path="/shopify/connect" element={<ShopifyInstallHandler />} />
      <Route path="/embedded-login" element={<EmbeddedAuthRedirect />} />
      
      {/* Site replacement confirmation */}
      <Route path="/confirm-site-replace" element={<ConfirmSiteReplace />} />

      {/* Protected routes */}
      <Route
        element={
          <AuthWrapper>
            <SiteProvider>
              <Outlet />
              <AppWarmup />
            </SiteProvider>
          </AuthWrapper>
        }
      >
        {/* Always accessible - NO subscription required */}
        <Route path="/home" element={<HomePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/billing" element={<SettingsPage />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/sites" element={<AddWebsitePage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/integrateGa4Only" element={<IntegrateGa4Only />} />

        {/* Setup/integration routes */}
        <Route path="/connect-shopify" element={<ConnectShopifyPage />} />
        <Route path="/connect-woocommerce" element={<ConnectWooCommerce />} />
        <Route path="/connect-bigcommerce" element={<ConnectBigCommerce />} />
        <Route path="/install-custom-script" element={<InstallCustomScript />} />
        <Route path="/optimize-site" element={<OptimizeSitePage />} />
        <Route path="/setup-stan-store" element={<SetUpStanStore />} />
        <Route path="/ConnectFailurePage" element={<ConnectFailurePage />} />
        <Route path="/ConnectSuccessPage" element={<ConnectSuccessPage />} />
        <Route path="/bigcommerce/load" element={<BigCommerceLanding />} />
        <Route path="/integrations/bigcommerce" element={<BigCommerceIntegrationsPage />} />
        <Route path="/link-bigcommerce-account" element={<LinkBigCommerceAccount />} />

        {/* Premium routes - REQUIRE subscription when enforcement enabled */}
        <Route element={<RequireSubscription><Outlet /></RequireSubscription>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route path="/sessions/:sessionId" element={<SessionDetail />} />
          <Route path="/dropoffs" element={<DropOffsPage />} />
          <Route path="/performance" element={<PerformancePage />} />
          <Route path="/business" element={<BusinessPage />} />
          <Route path="/facts" element={<FactsPage />} />
          <Route path="/customer-voice" element={<CustomerVoicePage />} />
          <Route path="/customerDashboard" element={<CustomerDashboard />} />
          <Route path="/UXInsightsPage" element={<UXInsightsPage />} />
          <Route path="/PerformanceDashboard" element={<PerformanceDashboard />} />
          <Route path="/reports" element={<ReportsPage />} />
        </Route>
      </Route>

      {/* Fallback to login */}
      <Route path="*" element={<LoginPage />} />
    </Routes>
  );

  return (
    <PolarisProvider i18n={{}}>
      <DataCacheProvider>
        <SubscriptionProvider>
          {isEmbedded ? (
            <NavigationProvider>
              {appRoutes}
            </NavigationProvider>
          ) : (
            appRoutes
          )}
        </SubscriptionProvider>
      </DataCacheProvider>
    </PolarisProvider>
  );
}

export default function App() {
  const [searchParams] = useSearchParams();
  const host = searchParams.get('host');
  const shop = searchParams.get('shop');
  
  // Store config globally if needed by App Bridge components
  if (host && shop) {
    window.shopifyAppBridgeConfig = {
      apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
      host: host,
      forceRedirect: true,
    };
  }

  return <AppContent />;
}